import { Box, Center, Flex, Span, Spinner, UnstyledButton } from "@givenwell/components";
import { styled } from "@givenwell/stitches";
import { colors } from "@givenwell/theme";
import { ReactNode, use, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { NavigationContext } from "../store";
import { Action, ModifierState, resolveAction } from "../types";
import { BottomBar } from "./BottomBar";

const NO_MODIFIERS: ModifierState = { shiftKey: false, altKey: false, ctrlKey: false, metaKey: false };

export type ListProps = {
  navigationTitle?: string;
  isLoading?: boolean;
  searchText?: string;
  onSearchTextChange?: (searchText: string) => void;
  searchBarPlaceholder?: string;
  content: ListItem[] | ListSection[];
};

export type ListSection = {
  title?: string;
  subtitle?: string;
  children: ListItem[];
};

export type ListItem = {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  actions: Action[];
};

export function List({
  navigationTitle,
  isLoading,
  searchBarPlaceholder,
  searchText: propsSearchText,
  onSearchTextChange,
  content,
}: ListProps) {
  const { pop, close, stack } = use(NavigationContext);
  const [highlightedIdx, _setHighlightedIdx] = useState(0);
  const [modifiers, setModifiers] = useState<ModifierState>(NO_MODIFIERS);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sync = (e: KeyboardEvent) =>
      setModifiers({ shiftKey: e.shiftKey, altKey: e.altKey, ctrlKey: e.ctrlKey, metaKey: e.metaKey });
    const reset = () => setModifiers(NO_MODIFIERS);
    window.addEventListener("keydown", sync);
    window.addEventListener("keyup", sync);
    window.addEventListener("blur", reset);
    return () => {
      window.removeEventListener("keydown", sync);
      window.removeEventListener("keyup", sync);
      window.removeEventListener("blur", reset);
    };
  }, []);

  const listSections: ListSection[] =
    content[0] && "children" in content[0] ? (content as ListSection[]) : [{ children: content as ListItem[] }];

  function setHighlightedIdx(idx: number) {
    flushSync(() => {
      _setHighlightedIdx(idx);
    });
    if (ref.current) {
      const highlightedItem = ref.current
        ?.querySelectorAll("[data-list-item]")
        .values()
        .find((_, i) => i === idx) as HTMLElement;
      highlightedItem?.scrollIntoView({ block: "nearest", behavior: "instant" });
      const itemBounds = highlightedItem.getBoundingClientRect();
      const scrollBounds = ref.current.getBoundingClientRect();
      const gapFromTop = scrollBounds.top + 8 - itemBounds.top;
      if (gapFromTop > 0) {
        ref.current.scrollBy({ behavior: "instant", top: -gapFromTop });
      }

      const distanceFromBottom = scrollBounds.top + scrollBounds.height - 40 - 9 - itemBounds.top - itemBounds.height;
      if (distanceFromBottom < 0) {
        ref.current.scrollBy({ behavior: "instant", top: -distanceFromBottom });
      }
    }
  }

  function getListItemCount() {
    return ref.current?.querySelectorAll("[data-list-item]").length || 1;
  }

  function getHighlightedListItemIndex(idx: number) {
    let sectionIndex = 0;
    for (const section of listSections) {
      if (section.children.length - 1 < idx) {
        idx -= section.children.length;
        sectionIndex++;
        continue;
      }
      return {
        section: sectionIndex,
        item: idx,
      };
    }
  }

  function getHighlightedListItem(idx: number) {
    for (const section of listSections) {
      if (section.children.length - 1 < idx) {
        idx -= section.children.length;
        continue;
      }
      return section.children[idx];
    }
    return undefined;
  }
  const highlightedListItem = getHighlightedListItem(highlightedIdx);

  const highlightedActions = highlightedListItem?.actions || [];

  const [localSearchText, _setLocalSearchText] = useState(propsSearchText || "");

  const searchText = propsSearchText ?? localSearchText;

  function setSearchText(text: string) {
    if (onSearchTextChange) {
      onSearchTextChange?.(text);
    } else {
      _setLocalSearchText(text);
    }
  }

  const indices = getHighlightedListItemIndex(highlightedIdx);

  return (
    <>
      <Header>
        <Input
          spellCheck="false"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          autoFocus
          className="ph-clear"
          placeholder={searchBarPlaceholder || "Search..."}
          onKeyDown={e => {
            const listItemCount = getListItemCount();
            if (e.key === "Escape") {
              e.preventDefault();
              if (searchText) {
                setSearchText("");
              } else {
                pop();
                if (!stack.length) {
                  close();
                }
              }
            } else if (e.key === "Backspace" && !e.currentTarget.value && !e.repeat) {
              e.preventDefault();
              pop();
            } else if (e.key === "Enter") {
              resolveAction(highlightedActions, e)?.onAction?.({ close });
            } else {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightedIdx((highlightedIdx + 1) % listItemCount);
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightedIdx((highlightedIdx - 1 + listItemCount) % listItemCount);
              } else if (!isModifier(e.key)) {
                setHighlightedIdx(0);
              }
            }
          }}
        />
        {isLoading && <Spinner css={{ size: 24, mr: 16 }} />}
      </Header>
      <Box ref={ref} css={{ flex: "1 0 0px", overflow: "auto", padding: 8, pb: 40 + 9, spaceY: 8 }}>
        {listSections.map((section, si) => (
          <ListSectionComp title={section.title} subtitle={section.subtitle}>
            {section.children?.map((item, i) => (
              <ListItemComp key={i} item={item} highlighted={si === indices?.section && i === indices.item} />
            ))}
          </ListSectionComp>
        ))}
      </Box>
      <BottomBar title={navigationTitle || ""} actions={highlightedActions} modifiers={modifiers} />
    </>
  );
}

function isModifier(key: string) {
  return ["Shift", "Control", "Alt", "Meta"].includes(key);
}

const Header = styled("div", {
  width: "100%",
  height: 56,
  display: "flex",
  items: "center",
  borderBottom: `${colors.outline} 1px solid`,
});

const Input = styled("input", {
  px: 16,
  flex: "1 0 0px",
  minWidth: 0,
  height: "100%",
  outline: "none",
  fontSize: 17,
  backgroundColor: "transparent",
  "&::placeholder": {
    color: colors.text2,
  },
});

function ListSectionComp({ title, subtitle, children }: { title?: string; subtitle?: string; children?: ReactNode }) {
  return (
    <Box>
      {title && (
        <Flex css={{ height: 28, fontScale: "sm", alignItems: "center", px: 8, color: colors.text2, weight: 500 }}>
          {title}
          {subtitle}
        </Flex>
      )}
      {children}
    </Box>
  );
}

export type ListItemProps = {
  item: ListItem;
  highlighted: boolean;
};

function ListItemComp({ item, highlighted }: ListItemProps) {
  const { close } = use(NavigationContext);
  return (
    <UnstyledButton
      data-highlighted={highlighted || undefined}
      data-list-item
      onClick={e => {
        resolveAction(item.actions, e)?.onAction?.({ close });
        close();
      }}
      css={{
        height: 40,
        width: "100%",
        display: "flex",
        items: "center",
        px: 8,
        borderRadius: 20 - 8,
        fontSize: 14,
        "&[data-highlighted]": { backgroundColor: colors.warmGrey },
      }}
    >
      {!!item.icon && <Center css={{ size: 24, minSize: 24, maxW: 24, mr: 12 }}>{item.icon}</Center>}
      {item.title}
      {item.subtitle && <Span css={{ color: colors.text2, marginLeft: 16 }}>{item.subtitle}</Span>}
    </UnstyledButton>
  );
}
